/**
 * The writes behind a `DealAction`.
 *
 * `stage.ts` says WHICH actions exist at a stage; this module performs them, so
 * every surface that offers an action runs the same writes in the same order.
 * The alternative — each screen wiring its own — is what shipped the defect
 * below.
 *
 * WHY `useRequestDataRoomAccess` EXISTS
 * ------------------------------------
 * "Request access" is TWO writes that must not come apart: the
 * `data_room_access_requests` row the founder answers, and the deal's move to
 * `awaiting_access`. When they lived in different handlers the product had both
 * halves and neither worked:
 *
 *   - The derived action button wrote only the STAGE. No request row, so the
 *     founder was never asked — and `awaiting_access` deliberately offers no
 *     way to re-request, so the deal was stranded there.
 *   - A second, identically-labelled button on startup-detail wrote only the
 *     ROW. The stage stayed `interested`, so when the founder approved,
 *     respond_to_data_room_request's `AND status = 'awaiting_access'` guard
 *     matched zero rows and the deal never entered `diligence`. Silently: the
 *     grandfathering in dd-entry.ts kept the card on the DD screen either way.
 *
 * ORDER IS LOAD-BEARING. The row is written FIRST and the stage only after it
 * resolves. A stage with no row is the stranding above; a row with no stage is
 * recoverable (the founder can still answer, and dd-entry.ts still lists the
 * deal). So the cheaper failure is the one left possible.
 */
import { useCallback } from 'react';

import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useRequestDataRoomAccessNew } from '@/lib/supabase';
import { requestAccessThenMoveStage } from './request-access-order';
import { useChangeStage } from './use-change-stage';

/**
 * The `request-access` action, whole: ask the founder, then move the deal to
 * `awaiting_access`.
 *
 * Rejects if the request row cannot be written, and the stage is NOT touched in
 * that case — callers should let the rejection reach their `handleError` and
 * show it. `useRequestDataRoomAccessNew`'s mutationFn throws on a supabase
 * error (`if (error) throw error`), so `mutateAsync` genuinely rejects here;
 * this is not a case where the failure hides in a returned `{ error }`.
 */
export function useRequestDataRoomAccess() {
  const changeStage = useChangeStage();
  const requestAccess = useRequestDataRoomAccessNew();

  const requestDataRoomAccess = useCallback(
    async ({
      dealId,
      startupId,
      investorProfileId,
      investorUserId,
    }: {
      /**
       * `dealflow.id` — the row whose stage moves, or null when there is no
       * pipeline row yet (a raising-feed item). The request row still stands on
       * its own in that case and `dd-entry.ts` still lists it; passing an empty
       * string instead would send a DB update keyed on nothing.
       */
      dealId: string | null;
      /** `startup_profiles.id` — who is being asked. */
      startupId: string;
      /** `investor_profiles.id` — as held on the request row. */
      investorProfileId: string;
      /**
       * `auth.users.id`. Only used to author the chat message announcing the
       * request, because the messages RLS INSERT policy checks
       * `sender_id = auth.uid()`. Omitting it still writes the request.
       */
      investorUserId?: string;
    }): Promise<void> => {
      await requestAccessThenMoveStage({
        writeRequestRow: () =>
          requestAccess.mutateAsync({
            startupId,
            investorId: investorProfileId,
            investorUserId,
          }),
        moveStage: () => {
          if (!dealId) return;
          // The stage the deal is ACTUALLY at, read from the same store the
          // move is about to write — not assumed to be `interested`. It is the
          // rollback target if the stage write is rejected, so a guess would
          // put the card somewhere it has never been.
          //
          // Read imperatively (`getState`, as `inverio-store.ts` does) rather
          // than through a selector: this runs once, after an await, inside a
          // callback. Subscribing the whole hook to the dealflow array would
          // re-render every screen holding it on any deal's change.
          const from = useDealflowStore
            .getState()
            .dealflow.find((d) => d.id === dealId)?.status;
          // No row in the store means nothing to move — and nothing to roll
          // back to. The founder's request row still stands on its own and
          // `dd-entry.ts` still lists it.
          if (!from) return;
          // The SAME gated path every other stage write takes. It used to be
          // `useMoveToAwaitingAccess`, an ungated write, because
          // `screening → awaiting_access` was illegal while the screen offered
          // it anyway; that transition is legal now and the exception is gone.
          //
          // The returned boolean is deliberately ignored. A refusal here means
          // the deal is somewhere the request is not a legal move (a terminal
          // stage), and the founder's request row — the half that matters — is
          // already written and still answerable. Failing the whole action
          // after the row exists would be the worse outcome, and the UI does
          // not offer the request from those stages anyway.
          changeStage(dealId, from, 'awaiting_access');
        },
        hasDealRow: dealId !== null && dealId !== '',
      });
    },
    [requestAccess, changeStage],
  );

  return { requestDataRoomAccess, isRequesting: requestAccess.isPending };
}
